package com.schoolconnect.app.feature.timetable

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppCard
import com.schoolconnect.app.designsystem.AppDropdownMenu
import com.schoolconnect.app.designsystem.AppInputBorder
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppSecondaryBackground
import com.schoolconnect.app.designsystem.AppSurface
import com.schoolconnect.app.designsystem.list.AdminListFooter
import com.schoolconnect.app.designsystem.list.AdminListActionIcons
import com.schoolconnect.app.designsystem.list.AdminListHeaderColumn
import com.schoolconnect.app.designsystem.list.AdminListPrimaryActionButton
import com.schoolconnect.app.designsystem.list.AdminListSearchField
import com.schoolconnect.app.designsystem.list.AdminListSortColumn
import com.schoolconnect.app.designsystem.list.AdminListSortHeaderRow
import com.schoolconnect.app.designsystem.list.AdminListTableBody
import com.schoolconnect.app.designsystem.list.AdminListTableCard
import com.schoolconnect.app.designsystem.list.AdminListTableShimmer
import com.schoolconnect.app.designsystem.list.AdminListToolbarButton
import com.schoolconnect.app.designsystem.list.AdminRowActionItem
import com.schoolconnect.app.designsystem.list.AdminRowActionMenu
import com.schoolconnect.app.feature.common.ShellPageHeader
import com.schoolconnect.app.feature.common.ShellTopRightActions
import com.schoolconnect.app.feature.common.shellAvatarInitials
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.common.shellRoleSubtitle
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.clock
import ischoolconnect.sharedui.generated.resources.eye
import ischoolconnect.sharedui.generated.resources.more_vertical
import ischoolconnect.sharedui.generated.resources.pencil
import ischoolconnect.sharedui.generated.resources.refresh_cw
import org.jetbrains.compose.resources.painterResource
import com.schoolconnect.app.datasource.network.response.TimetableViewGridCellResponse
import com.schoolconnect.app.datasource.network.response.TimetableViewResponse

private val TimeTableBg = AppSecondaryBackground
private val TimeTableStatusGreen = Color(0xFF16A34A)
private val TimeTableStatusOrange = Color(0xFFF59E0B)
private val TimeTableStatusRed = Color(0xFFDC2626)
private val TimeTableStatusMuted = Color(0xFF9CA3AF)

@Composable
fun TimeTableScreen(
    state: TimeTableUiState,
    onEvent: (TimeTableUiEvent) -> Unit,
    schoolLabel: String,
    academicYearLabel: String,
    headerActions: @Composable RowScope.() -> Unit,
    readOnly: Boolean = false,
    contentHorizontalPadding: Dp = 24.dp,
    contentVerticalPadding: Dp = 24.dp,
    modifier: Modifier = Modifier
) {
    var rowsMenu by remember { mutableStateOf(false) }
    var showEditor by remember { mutableStateOf(false) }
    var editorClassSectionId by remember { mutableStateOf<String?>(null) }
    var editorTimetableHeaderId by remember { mutableStateOf<String?>(null) }
    var activeEditorClassSectionId by remember { mutableStateOf<String?>(null) }
    var viewRow by remember { mutableStateOf<TimeTableRow?>(null) }
    val filteredRows = state.rows.filter { row ->
        (state.searchQuery.isBlank() || row.classSection.contains(state.searchQuery.trim(), ignoreCase = true)) &&
            (state.classFilter.isBlank() || row.classSection == state.classFilter) &&
            (state.statusFilter.isBlank() || row.status == state.statusFilter)
    }
    val hasLocalOnlyFilter = state.searchQuery.isNotBlank() || state.statusFilter.isNotBlank()
    val visibleRows = if (hasLocalOnlyFilter) filteredRows else state.rows
    val displayTotal = if (hasLocalOnlyFilter) visibleRows.size else state.totalCount
    val pageCount = ((displayTotal + state.pageSize - 1) / state.pageSize).coerceAtLeast(1)
    val from = if (displayTotal == 0) 0 else state.pageIndex * state.pageSize + 1
    val to = if (from == 0) 0 else minOf(from + visibleRows.size - 1, displayTotal)

    Column(
        modifier = modifier.fillMaxSize().background(TimeTableBg).padding(
            horizontal = contentHorizontalPadding,
            vertical = contentVerticalPadding
        ),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        ShellPageHeader(
            title = "Time Table",
            subtitle = if (readOnly) "View class timetables." else "Create and manage class timetables.",
            detailText = "$schoolLabel | $academicYearLabel",
            titleColor = AppPageHeader,
            subtitleColor = AppPageSubtitle,
            detailColor = AppMuted,
            headerActions = headerActions
        )

        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val compact = maxWidth < 820.dp
            FilterPanel(state, onEvent, compact)
        }

        AdminListTableCard(
            modifier = Modifier.fillMaxWidth().weight(1f),
            minContentWidth = 820.dp,
            header = {
                AdminListSortHeaderRow(
                    columns = listOf(
                        AdminListSortColumn("Class / Section", 1.15f, state.sortBy == "class_name", state.sortAscending, onClick = { onEvent(TimeTableUiEvent.SortChanged("class_name")) }),
                        AdminListSortColumn("Status", 1.2f, state.sortBy == "status", state.sortAscending, onClick = { onEvent(TimeTableUiEvent.SortChanged("status")) }),
                        AdminListSortColumn("Last Updated", 1.1f, state.sortBy == "updated_at", state.sortAscending, onClick = { onEvent(TimeTableUiEvent.SortChanged("updated_at")) }),
                        AdminListSortColumn("Last Updated By", 1.1f, false, true, sortable = false),
                    ),
                    actionsLabel = "Actions",
                    actionsWeight = .95f,
                    horizontalPadding = 16.dp,
                    height = 44.dp
                )
            },
            body = {
                if (state.isLoading) {
                    AdminListTableShimmer(
                        columns = listOf(
                            AdminListHeaderColumn("Class / Section", 1.15f),
                            AdminListHeaderColumn("Status", 1.2f),
                            AdminListHeaderColumn("Last Updated", 1.1f),
                            AdminListHeaderColumn("Last Updated By", 1.1f),
                            AdminListHeaderColumn("Actions", .95f)
                        ),
                        rowCount = state.pageSize,
                        modifier = Modifier.fillMaxSize(),
                        horizontalPadding = 20.dp
                    )
                } else if (visibleRows.isEmpty()) {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text("No class timetables found", color = AppMuted, fontSize = 14.sp)
                    }
                } else {
                    AdminListTableBody(items = visibleRows, key = { it.id }, modifier = Modifier.fillMaxSize()) { row ->
                        TimeTableRowItem(
                            row,
                            state.selectedRowId == row.id,
                            onEvent,
                            onView = { viewRow = row; onEvent(TimeTableUiEvent.ViewRequested(row)) },
                            onEnter = if (readOnly) null else {
                                {
                                    editorClassSectionId = row.classSectionId
                                    editorTimetableHeaderId = row.headerId
                                    activeEditorClassSectionId = row.classSectionId
                                    onEvent(TimeTableUiEvent.ViewRequested(row))
                                    showEditor = true
                                }
                            }
                        )
                        if (row.id != visibleRows.last().id) HorizontalDivider(color = AppBorder)
                    }
                }
            },
            footer = {
                AdminListFooter(
                    pageText = "Showing $from to $to of ${state.totalCount} class-sections",
                    pageIndex = state.pageIndex,
                    pageCount = pageCount,
                    onPageChanged = { onEvent(TimeTableUiEvent.PageChanged(it)) },
                    pageSize = state.pageSize,
                    rowsPerPageExpanded = rowsMenu,
                    onRowsPerPageToggle = { rowsMenu = !rowsMenu },
                    onRowsPerPageDismiss = { rowsMenu = false },
                    onPageSizeChanged = { onEvent(TimeTableUiEvent.PageSizeChanged(it)) },
                    footerColor = AppMuted,
                    borderColor = AppBorder,
                    surfaceColor = AppSurface,
                    primaryColor = AppPrimary,
                    navyColor = AppNavy,
                    mutedColor = AppMuted
                )
            }
        )

        if (!readOnly) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                AdminListPrimaryActionButton(
                    label = "Enter Time Table",
                    icon = painterResource(Res.drawable.pencil),
                    onClick = { editorClassSectionId = null; editorTimetableHeaderId = null; activeEditorClassSectionId = null; showEditor = true },
                    backgroundColor = AppPrimary
                )
            }
        }
    }
    if (showEditor) {
        EnterTimeTableDialog(
            schoolLabel = schoolLabel,
            classSectionId = editorClassSectionId,
            timetableHeaderId = if (activeEditorClassSectionId != null && state.editorTimetableClassSectionId == activeEditorClassSectionId) state.editorTimetableHeaderId else editorTimetableHeaderId,
            initialTimetable = if (activeEditorClassSectionId != null && state.editorTimetableClassSectionId == activeEditorClassSectionId) state.editorTimetable else state.viewDetails?.takeIf { it.id == editorTimetableHeaderId },
            timetableLoading = state.isEditorTimetableLoading && state.editorTimetableClassSectionId == activeEditorClassSectionId,
            timetableError = listOf(
                state.editorTimetableError.takeIf { state.editorTimetableClassSectionId == activeEditorClassSectionId },
                state.editorSaveError.takeIf { state.editorTimetableClassSectionId == activeEditorClassSectionId }
            ).filterNot { it.isNullOrBlank() }.joinToString("\n").orEmpty(),
            classSections = state.classSections,
            subjects = state.timetableSubjects,
            subjectsLoading = state.areTimetableSubjectsLoading,
            subjectsError = state.timetableSubjectsError,
            academicYearStartDate = state.academicYearStartDate,
            academicYearEndDate = state.academicYearEndDate,
            academicYearLoading = state.isAcademicYearLoading,
            academicYearError = state.academicYearError,
            onClassSectionSelected = {
                activeEditorClassSectionId = it
                onEvent(TimeTableUiEvent.ClassSectionSelected(it))
            },
            onSave = { headerId, request, onSuccess ->
                onEvent(TimeTableUiEvent.TimetableSaved(headerId, request, onSuccess))
            },
            onDismiss = { showEditor = false }
        )
    }
    viewRow?.let { row ->
        val details = state.viewDetails
        PreviewTimeTableDialog(
            selectedClass = details?.className ?: row.classSection.substringBefore("-"),
            selectedSection = details?.sectionName ?: row.classSection.substringAfter("-"),
            fromDate = details?.let { formatPreviewDate(it.fromDate) } ?: "—",
            duration = details?.let { "to ${formatPreviewDate(it.toDate)}" } ?: "—",
            slots = details?.toPreviewSlots().orEmpty(),
            values = details?.toPreviewValues().orEmpty(),
            specialSchedules = details?.toSpecialSchedules().orEmpty(),
            specialDays = details?.toSpecialDays().orEmpty(),
            weeklyOffDays = details?.toWeeklyOffDays().orEmpty(),
            weekStartDate = details?.let { formatPreviewDate(it.fromDate) } ?: "",
            status = details?.status ?: if (state.isViewLoading) "Loading" else "Not Published",
            enableWeekNavigation = true,
            title = when {
                state.isViewLoading -> "Loading Time Table"
                state.viewErrorMessage.isNotBlank() -> "No Time Table"
                else -> "View Time Table"
            },
            subtitle = when {
                state.isViewLoading -> "Loading the latest timetable for the selected class."
                state.viewErrorMessage.isNotBlank() -> state.viewErrorMessage
                else -> "View the published timetable for the selected class."
            },
            onDismiss = { viewRow = null; onEvent(TimeTableUiEvent.ViewDismissed) }
        )
    }
}

internal val PreviewDayNames = listOf("Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday")

internal fun TimetableViewResponse.toPreviewSlots(): List<EditorSlot> = slots
    .filter { it.scheduleType?.uppercase() != "SPECIAL" }
    .sortedBy { it.slotOrder }
    .mapNotNull { slot ->
        val start = formatTimetableTimeForDisplay(slot.fromTime)
        val end = formatTimetableTimeForDisplay(slot.toTime)
        val label = listOfNotNull(start, end).joinToString(" – ")
        label.takeIf(String::isNotBlank)?.let(::EditorSlot)
    }
    .ifEmpty { regularGrid.sortedBy { it.slotOrder }.map { EditorSlot(formatTimetableRangeForDisplay(it.timeRange)) } }

private fun formatTimetableTimeForDisplay(value: String?): String? {
    val parts = value?.trim()?.uppercase()?.split(Regex("\\s+")) ?: return null
    val clockParts = parts.firstOrNull()?.split(":") ?: return null
    if (clockParts.size < 2) return value.trim().takeIf(String::isNotBlank)
    val rawHour = clockParts[0].toIntOrNull() ?: return value.trim().takeIf(String::isNotBlank)
    val minute = clockParts[1].take(2).toIntOrNull()?.takeIf { it in 0..59 }
        ?: return value.trim().takeIf(String::isNotBlank)
    val meridiem = parts.getOrNull(1)
    val hour24 = when (meridiem) {
        "AM" -> rawHour.takeIf { it in 1..12 }?.let { it % 12 }
        "PM" -> rawHour.takeIf { it in 1..12 }?.let { it % 12 + 12 }
        null -> rawHour.takeIf { it in 0..23 }
        else -> null
    } ?: return value.trim().takeIf(String::isNotBlank)
    val hour12 = (hour24 % 12).let { if (it == 0) 12 else it }
    val suffix = if (hour24 < 12) "AM" else "PM"
    return "${hour12.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} $suffix"
}

private fun formatTimetableRangeForDisplay(value: String): String {
    val parts = value.split("–", "-", limit = 2)
    if (parts.size != 2) return value
    val start = formatTimetableTimeForDisplay(parts[0]) ?: parts[0].trim()
    val end = formatTimetableTimeForDisplay(parts[1]) ?: parts[1].trim()
    return "$start – $end"
}

internal fun TimetableViewResponse.toPreviewValues(): List<String> {
    val previewSlots = toPreviewSlots()
    val gridRows = regularGrid.sortedBy { it.slotOrder }
    return PreviewDayNames.indices.flatMap { dayIndex ->
        previewSlots.indices.map { slotIndex ->
            val row = gridRows.firstOrNull { it.slotOrder == slotIndex + 1 } ?: gridRows.getOrNull(slotIndex)
            row?.cells?.firstOrNull { it.toPreviewDayIndex() == dayIndex }
                ?.entryText.orEmpty()
        }
    }
}

internal fun TimetableViewResponse.toSpecialSchedules(): List<SpecialDaySchedule> {
    val days = toSpecialDays()
    if (days.isEmpty()) return emptyList()
    val rowsByOrder = specialGrid.associateBy { it.slotOrder }
    val specialSlots = slots
        .filter { it.scheduleType.equals("SPECIAL", ignoreCase = true) }
        .sortedBy { it.slotOrder }
    val rowOrders = (specialSlots.map { it.slotOrder } + specialGrid.map { it.slotOrder }).distinct().sorted()
    val editorSlots = rowOrders.mapNotNull { order ->
        val slot = specialSlots.firstOrNull { it.slotOrder == order }
        val row = rowsByOrder[order]
        val label = if (slot != null) {
            listOfNotNull(
                formatTimetableTimeForDisplay(slot.fromTime),
                formatTimetableTimeForDisplay(slot.toTime)
            ).joinToString(" – ")
        } else row?.let { formatTimetableRangeForDisplay(it.timeRange) }.orEmpty()
        label.takeIf(String::isNotBlank)?.let(::EditorSlot)
    }
    return days.map { day ->
        val dayIndex = PreviewDayNames.indexOf(day)
        SpecialDaySchedule(
            day = day,
            slots = editorSlots,
            values = rowOrders.map { order ->
                rowsByOrder[order]?.cells?.firstOrNull { it.toPreviewDayIndex() == dayIndex }?.entryText.orEmpty()
            }
        )
    }
}

internal fun TimetableViewResponse.toSpecialDays(): List<String> = specialScheduleDays
    .distinct()
    .sorted()
    .mapNotNull { PreviewDayNames.getOrNull(it) }

internal fun TimetableViewResponse.toWeeklyOffDays(): List<String> = weeklyOffDays
    .distinct()
    .sorted()
    .mapNotNull { PreviewDayNames.getOrNull(it) }

private fun TimetableViewGridCellResponse.toPreviewDayIndex(): Int {
    val namedDayIndex = PreviewDayNames.indexOfFirst { it.equals(dayName.trim(), ignoreCase = true) }
    if (namedDayIndex >= 0) return namedDayIndex
    return dayOfWeek.coerceIn(0, 6)
}

private fun Int.toPreviewDayIndex(): Int = coerceIn(0, 6)

internal fun formatPreviewDate(value: String?): String {
    val parts = value.orEmpty().substringBefore('T').split('-')
    if (parts.size != 3) return value?.ifBlank { "—" } ?: "—"
    val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val month = parts[1].toIntOrNull()?.let { months.getOrNull(it - 1) } ?: return value.orEmpty()
    return "${parts[2].padStart(2, '0')} $month ${parts[0]}"
}

internal fun formatTimetableDayDate(weekStartDate: String, dayName: String): String {
    val input = weekStartDate.trim().substringBefore('T')
    val isoParts = input.split('-')
    val displayParts = input.split(' ')
    val date: LocalDate = runCatching<LocalDate> {
        if (isoParts.size == 3) {
            LocalDate(isoParts[0].toInt(), isoParts[1].toInt(), isoParts[2].toInt())
        } else if (displayParts.size == 3) {
            val months = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
            val month = months.indexOfFirst { it.equals(displayParts[1], ignoreCase = true) } + 1
            LocalDate(displayParts[2].toInt(), month, displayParts[0].toInt())
        } else throw IllegalArgumentException("Unsupported date format")
    }.getOrNull() ?: return ""
    val timetableDayIndex = PreviewDayNames.indexOfFirst { it.startsWith(dayName.trim(), ignoreCase = true) }
    if (timetableDayIndex < 0) return ""
    val monday = date.minus(date.dayOfWeek.isoDayNumber - 1, DateTimeUnit.DAY)
    val dayDate = monday.plus(timetableDayIndex, DateTimeUnit.DAY)
    val monthLabel = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[dayDate.monthNumber - 1]
    return "${dayDate.dayOfMonth.toString().padStart(2, '0')} $monthLabel"
}

@Composable
private fun FilterPanel(state: TimeTableUiState, onEvent: (TimeTableUiEvent) -> Unit, compact: Boolean) {
    AppCard(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)), containerColor = Color.White) {
        if (compact) {
            Column(Modifier.fillMaxWidth().padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                AdminListSearchField(state.searchQuery, { onEvent(TimeTableUiEvent.SearchChanged(it)) }, "Search by Class / Section", modifier = Modifier.fillMaxWidth(), width = Dp.Unspecified, height = 44.dp)
                TimeTableSelect("Class / Section", state.classFilter.ifBlank { "All Classes / Sections" }, state.classSections.map { listOf(it.className, it.sectionName).filter(String::isNotBlank).joinToString("-") }.filter(String::isNotBlank), Modifier.fillMaxWidth()) { onEvent(TimeTableUiEvent.ClassFilterChanged(it)) }
                TimeTableSelect("Status", state.statusFilter.ifBlank { "All" }, timetableStatusLabels(), Modifier.fillMaxWidth()) { onEvent(TimeTableUiEvent.StatusFilterChanged(it)) }
                Text("Clear filters", color = AppPrimary, fontSize = 14.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.clickable { onEvent(TimeTableUiEvent.FiltersCleared) })
            }
        } else {
            Row(Modifier.fillMaxWidth().padding(16.dp), horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Bottom) {
                Column(Modifier.weight(1.45f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                    Text("Search by Class / Section", color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                    AdminListSearchField(state.searchQuery, { onEvent(TimeTableUiEvent.SearchChanged(it)) }, "Search by Class / Section", modifier = Modifier.fillMaxWidth(), width = Dp.Unspecified, height = 44.dp)
                }
                TimeTableSelect("Class / Section", state.classFilter.ifBlank { "All Classes / Sections" }, state.classSections.map { listOf(it.className, it.sectionName).filter(String::isNotBlank).joinToString("-") }.filter(String::isNotBlank), Modifier.weight(1f)) { onEvent(TimeTableUiEvent.ClassFilterChanged(it)) }
                TimeTableSelect("Status", state.statusFilter.ifBlank { "All" }, timetableStatusLabels(), Modifier.weight(.72f)) { onEvent(TimeTableUiEvent.StatusFilterChanged(it)) }
                AdminListToolbarButton(label = "Clear", icon = painterResource(Res.drawable.refresh_cw), onClick = { onEvent(TimeTableUiEvent.FiltersCleared) }, width = 116.dp, height = 44.dp, surfaceColor = AppSurface, borderColor = AppInputBorder, contentColor = AppPrimary)
            }
        }
    }
}

@Composable
private fun TimeTableClearButton(onClick: () -> Unit) {
    Box(
        Modifier.width(84.dp).height(44.dp)
            .border(1.dp, AppBorder, RoundedCornerShape(8.dp))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Text("Clear", color = AppPrimary, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
    }
}

@Composable
private fun TimeTableSelect(label: String, value: String, options: List<String>, modifier: Modifier, onSelected: (String) -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    Column(modifier, verticalArrangement = Arrangement.spacedBy(7.dp)) {
        Text(label, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        Box(Modifier.fillMaxWidth().height(44.dp).background(AppSurface, RoundedCornerShape(8.dp)).border(1.dp, AppBorder, RoundedCornerShape(8.dp)).clickable { expanded = true }.padding(horizontal = 14.dp), contentAlignment = Alignment.CenterStart) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(value, color = AppNavy, fontSize = 14.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Icon(painterResource(Res.drawable.chevron_down), null, tint = AppNavy, modifier = Modifier.size(18.dp))
            }
            AppDropdownMenu(expanded, { expanded = false }, containerColor = Color.White, border = BorderStroke(1.dp, AppBorder)) {
                DropdownMenuItem(text = { Text("All", color = AppNavy) }, onClick = { expanded = false; onSelected("") })
                options.forEach { option -> DropdownMenuItem(text = { Text(option, color = AppNavy) }, onClick = { expanded = false; onSelected(option) }) }
            }
        }
    }
}

@Composable
private fun TimeTableRowItem(row: TimeTableRow, actionsExpanded: Boolean, onEvent: (TimeTableUiEvent) -> Unit, onView: () -> Unit, onEnter: (() -> Unit)?) {
    Box(Modifier.fillMaxWidth()) {
        Row(Modifier.fillMaxWidth().heightIn(min = 52.dp).padding(horizontal = 16.dp, vertical = 4.dp), verticalAlignment = Alignment.CenterVertically) {
            Text(row.classSection, color = AppNavy, fontSize = 14.sp, modifier = Modifier.weight(1.15f))
            Text(row.status.toTimetableStatusDisplay(), color = row.status.toTimetableStatusColor(), fontSize = 14.sp, modifier = Modifier.weight(1.2f), maxLines = 1, overflow = TextOverflow.Ellipsis)
            Text(row.lastUpdated, color = AppNavy, fontSize = 14.sp, modifier = Modifier.weight(1.1f))
            Text(row.lastUpdatedBy, color = AppNavy, fontSize = 14.sp, modifier = Modifier.weight(1.1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
            Box(Modifier.weight(.95f), contentAlignment = Alignment.Center) {
                AdminListActionIcons(onView = onView, onEdit = onEnter) {}
            }
        }
    }
}

private fun timetableStatusLabels(): List<String> = SystemLookupOptions.labels("TIMETABLE_STATUS").ifEmpty {
    listOf(
        TimetableStatusValues.PUBLISHED,
        TimetableStatusValues.CHANGES_PENDING,
        TimetableStatusValues.NOT_PUBLISHED,
        TimetableStatusValues.BLANK
    )
}

private fun String.toTimetableStatusDisplay(): String = when (trim()) {
    TimetableStatusValues.PUBLISHED -> TimetableStatusValues.PUBLISHED
    TimetableStatusValues.CHANGES_PENDING, "Published - Changes Pending" -> TimetableStatusValues.CHANGES_PENDING
    TimetableStatusValues.NOT_PUBLISHED -> TimetableStatusValues.NOT_PUBLISHED
    TimetableStatusValues.BLANK -> "—"
    else -> ifBlank { "—" }
}

private fun String.toTimetableStatusColor(): Color = when (trim()) {
    TimetableStatusValues.PUBLISHED -> TimeTableStatusGreen
    TimetableStatusValues.CHANGES_PENDING, "Published - Changes Pending" -> TimeTableStatusOrange
    TimetableStatusValues.NOT_PUBLISHED -> TimeTableStatusRed
    else -> TimeTableStatusMuted
}
